import type { ChargeLineResponse } from "@/services/core-api/types"
import { sumDecimalStrings } from "@/utils/sum-decimal-strings"
import { DateTime } from "luxon"

export type BillChargeLine = Pick<
  ChargeLineResponse,
  | "amount"
  | "contributes_to_total"
  | "currency"
  | "exclusion_reason"
  | "group_heading"
  | "id"
  | "label"
  | "quantity"
  | "rate"
  | "service_end"
  | "service_start"
  | "sort_order"
  | "unit"
>

export interface BillChargeGroup {
  currency: string | null
  isMiscellaneous: boolean
  lines: BillChargeLine[]
  title: string
  total: string
}

const sumContributingAmounts = (lines: readonly BillChargeLine[]): string =>
  sumDecimalStrings(
    lines.flatMap((line) => (line.contributes_to_total ? [line.amount] : []))
  )

const getGroupCurrency = (lines: BillChargeLine[]): string | null =>
  lines.find((line) => line.currency)?.currency ?? null

const isOmittedChargeLine = (line: BillChargeLine): boolean =>
  line.exclusion_reason === "subtotal" ||
  line.exclusion_reason === "issuer_non_contributing"

const toChargeDate = (value: string | null): DateTime | null => {
  if (value == null || value.trim() === "") {
    return null
  }

  const date = DateTime.fromISO(value.trim())
  return date.isValid ? date : null
}

const laterChargeDate = (
  left: DateTime | null,
  right: DateTime | null
): DateTime | null => {
  if (left == null) {
    return right
  }

  if (right == null || left.toMillis() >= right.toMillis()) {
    return left
  }

  return right
}

const earlierChargeDate = (
  left: DateTime | null,
  right: DateTime | null
): DateTime | null => {
  if (left == null) {
    return right
  }

  if (right == null || left.toMillis() <= right.toMillis()) {
    return left
  }

  return right
}

interface PeriodSortKey {
  end: number
  start: number
}

const periodSortKey = (line: BillChargeLine): PeriodSortKey | null => {
  const start = toChargeDate(line.service_start)
  const end = toChargeDate(line.service_end)
  const latest = laterChargeDate(start, end)
  if (latest == null) {
    return null
  }

  return {
    end: latest.toMillis(),
    start: (earlierChargeDate(start, end) ?? latest).toMillis(),
  }
}

const compareLinesByPeriod = (
  left: BillChargeLine,
  right: BillChargeLine
): number => {
  const leftKey = periodSortKey(left)
  const rightKey = periodSortKey(right)

  if (leftKey == null && rightKey == null) {
    return left.sort_order - right.sort_order
  }

  if (leftKey == null) {
    return 1
  }

  if (rightKey == null) {
    return -1
  }

  if (leftKey.end !== rightKey.end) {
    return rightKey.end < leftKey.end ? -1 : 1
  }

  if (leftKey.start !== rightKey.start) {
    return rightKey.start < leftKey.start ? -1 : 1
  }

  return left.sort_order - right.sort_order
}

const sortLinesByPeriod = (
  lines: readonly BillChargeLine[]
): BillChargeLine[] =>
  [...lines].sort((left, right) => compareLinesByPeriod(left, right))

const toGroup = (
  lines: BillChargeLine[],
  title: string,
  isMiscellaneous: boolean
): BillChargeGroup => ({
  currency: getGroupCurrency(lines),
  isMiscellaneous,
  lines: sortLinesByPeriod(lines),
  title,
  total: sumContributingAmounts(lines),
})

/**
 * Groups charge lines by the printed heading. A printed subtotal or an
 * issuer-excluded line is omitted.
 * A null or blank heading on a remaining line is Miscellaneous and is last.
 * Named groups follow the first printed occurrence of a remaining line.
 * Lines in a group are newest period first. A line with no period is last.
 * The same period keeps printed order.
 * A group with no remaining lines is absent. The total includes only lines
 * that contribute.
 */
export const groupBillChargeLines = (
  lines: readonly BillChargeLine[]
): BillChargeGroup[] => {
  const ordered = [...lines].sort(
    (left, right) => left.sort_order - right.sort_order
  )
  const namedGroups = new Map<string, BillChargeLine[]>()
  const miscellaneous: BillChargeLine[] = []

  for (const line of ordered) {
    if (isOmittedChargeLine(line)) {
      continue
    }

    const heading = line.group_heading
    if (heading == null || heading.trim() === "") {
      miscellaneous.push(line)
      continue
    }

    const existing = namedGroups.get(heading)
    if (existing) {
      existing.push(line)
    } else {
      namedGroups.set(heading, [line])
    }
  }

  const groups = [...namedGroups.entries()].map(([title, groupLines]) =>
    toGroup(groupLines, title, false)
  )

  if (miscellaneous.length > 0) {
    groups.push(toGroup(miscellaneous, "", true))
  }

  return groups
}
